import { useMemo } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { useProfileStore } from '../store/useProfileStore';
import { ProfileSnapshot, profileToSnapshot } from '../services/profileSnapshot';

/**
 * Returns the minimum profile shape (id, displayName, avatarUrl, invitedById,
 * metadata.onboarding.selectedRole) needed to paint the logged-in shell on
 * public routes — TopBar avatar, HomePage chef-vs-consumer paint,
 * ListingDetailsPage connection-level check.
 *
 * Resolution order:
 *  1. Anonymous (`!isAuthenticated`) → null. TopBar shows Sign In, etc.
 *  2. Authenticated + `activeProfile` populated (post-whoami) → projected
 *     from the live profile.
 *  3. Authenticated + `activeProfile` still null (boot window before whoami
 *     returns) → the localStorage-hydrated snapshot.
 *
 * Step 3 is the load-bearing case: without it, a returning user sees the
 * unauth shell flash for ~600ms before whoami populates `activeProfile`.
 *
 * The returned shape is intentionally a subset of `Profile`. Consumers that
 * need fields outside that subset (email, phone, addresses, etc.) must read
 * `activeProfile` directly and tolerate the null-while-loading window —
 * AuthGuard already covers that case for ProtectedRoute children.
 */
export function useDisplayProfile(): ProfileSnapshot | null {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const displaySnapshot = useProfileStore((s) => s.displaySnapshot);
  return useMemo(() => {
    if (!isAuthenticated) return null;
    if (activeProfile) return profileToSnapshot(activeProfile);
    return displaySnapshot;
  }, [isAuthenticated, activeProfile, displaySnapshot]);
}
